import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GedPrepDisclaimer } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

const GED_COPY = {
  base: { title: "GED Prep unlocked", body: "You've got 12 months of GED courses, lessons and AI tutoring. Head to the GED Prep page to start studying.", cta: "Open GED Prep", to: "/ged" },
  pack: { title: "Practice tests added", body: "One full-length attempt has been added for each of the 4 subjects. Pick a subject and start whenever you're ready.", cta: "Open GED Prep", to: "/ged" },
  retest: { title: "Retake added", body: "A new practice-test attempt has been added. Head back and start it when you're ready.", cta: "Open GED Prep", to: "/ged" },
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshBilling } = useAuth();
  const [state, setState] = useState("checking");
  const [info, setInfo] = useState(null);
  const tries = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      setState("error");
      return;
    }
    let timer;
    const check = async () => {
      tries.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          await refreshBilling();
          setState("done");
          return;
        }
        if (data.status === "expired") {
          setState("error");
          return;
        }
      } catch {
        /* retry */
      }
      if (tries.current < 15) timer = setTimeout(check, 2000);
      else setState("timeout");
    };
    check();
    return () => clearTimeout(timer);
  }, [params, refreshBilling]);

  const ged = info?.ged_product ? GED_COPY[info.ged_product] : null;

  return (
    <div className="max-w-lg mx-auto mt-10 bg-white border border-border rounded-[18px] p-10 text-center fade-up" data-testid="payment-success">
      {state === "checking" && (<><Loader2 className="h-10 w-10 animate-spin mx-auto text-[#0F7B7D]" /><h1 className="mt-4 text-2xl font-display font-semibold">Confirming your payment...</h1></>)}
      {state === "done" && (
        <>
          <CheckCircle2 className="h-12 w-12 mx-auto text-[#1F9D55]" />
          <h1 className="mt-4 text-2xl font-display font-semibold" data-testid="payment-success-title">{ged ? ged.title : "You're all set!"}</h1>
          <p className="mt-2 text-muted-foreground">{ged ? ged.body : "Your 30-day free trial has started. Everything is unlocked."}</p>
          {ged && <GedPrepDisclaimer className="mt-5 text-left" />}
          <Button asChild className="mt-6 h-11" data-testid="payment-success-continue">
            <Link to={ged ? ged.to : "/dashboard"}>{ged ? ged.cta : "Go to dashboard"}</Link>
          </Button>
        </>
      )}
      {(state === "error" || state === "timeout") && (
        <>
          <XCircle className="h-12 w-12 mx-auto text-[#D64545]" />
          <h1 className="mt-4 text-2xl font-display font-semibold">{state === "timeout" ? "Still processing" : "We couldn't confirm your payment"}</h1>
          <p className="mt-2 text-muted-foreground">{state === "timeout" ? "This can take a minute. Check your Billing page shortly." : "No charge was made. Please try again."}</p>
          <Button asChild className="mt-6 h-11"><Link to="/billing">Back to billing</Link></Button>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-lg mx-auto mt-10 bg-white border border-border rounded-[18px] p-10 text-center fade-up" data-testid="payment-cancel">
      <XCircle className="h-12 w-12 mx-auto text-muted-foreground" />
      <h1 className="mt-4 text-2xl font-display font-semibold">Checkout canceled</h1>
      <p className="mt-2 text-muted-foreground">No worries — nothing was charged.</p>
      <Button asChild className="mt-6 h-11"><Link to="/billing">Back to plans</Link></Button>
    </div>
  );
}
